import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Canvas } from '@react-three/fiber';
import Scribble from '../../Utils/Scribble.js';
import SceneComponent from './SceneComponent';
import Measurements from '../../clickTools/Measurements.js';
import Info from '../../clickTools/Info.js';
import ColorChange from '../../clickTools/ColorChange.js';
import ObjectDetails from '../../clickTools/ObjectDetails.js';
import { useProgress } from '@react-three/drei';
import { SceneSlicer } from '../SceneSlicer/index.jsx';
import UnifiedPanel from './UnifiedPanel.js';
import ScaleBar from '../ui/ScaleBar';
import SceneTopbarTools from './SceneTopbarTools.js';
import SceneBackgroundStrip from '../ui/SceneBackgroundStrip';
import SceneLookStrip from '../ui/SceneLookStrip';
import PreferencesStrip from '../ui/PreferencesStrip';
import SceneDataPanel from '../ui/SceneDataPanel';
import HoleLegendStack from '../ui/HoleLegendStack';
import { SubmenuRail, useSubmenuLayout } from '../ui/ToolSubmenu';
import DraggablePanel from '../ui/DraggablePanel';
import MobileNav, { MobileMoreList } from '../ui/MobileNav';
import MobileSheet from '../ui/MobileSheet';
import { readCompactLayout, useCompactLayout } from '../../hooks/useCompactLayout';
import { useClickStore } from '../../store/clickStore.js';
import { useSectionStore } from '../../store/sectionStore';
import { useShallow } from 'zustand/react/shallow';
import { useLoadingTracker } from '../../contexts/LoadingTrackerContext';
import { useAuth } from '../../contexts/AuthContext';
import FileManagement from './FileManagement.js';
import { useModelCatalogue } from '../../hooks/useModelCatalogue';
import { useHoleQueryStore } from '../../store/holeQueryStore';
import { hydrateHoleRecordStore } from '../../store/holeRecordStore';
import { mergeHoleQueryLayers } from '../../Utils/holeQuery';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { useSceneToolShortcuts } from '../../hooks/useSceneToolShortcuts';
import { resolveLayerKey, collectPrecacheUrls, modelCacheUrls, modelFileChanged } from '../../Utils/layerKeys';
import {
  activateAppUpdate,
  fetchRemoteVersion,
  getStoredVersion,
  invalidateModelPath,
  isRemoteNewer,
  listCachedModelPaths,
  needsFirstTimeWipe,
  performFirstTimeWipe,
  precacheUrls,
  precacheAppAssets,
  decoderAssetUrls,
  setOfflineReady,
  urlIsInCachedPaths,
} from '../../Utils/offlineCache';
import { ENV_CONFIG } from '../../config/environment';
import { getSite } from '../../config/siteConfig';
import { sceneBackgroundCss } from '../../Utils/sceneBackground';
import { formatBytes } from '../../Utils/fetchWithProgress';
import { resolveSceneLook } from '../../Utils/sceneLooks';
import { readSceneDataPanelOpen, writeSceneDataPanelOpen } from '../../Utils/scenePerf';
import { downloadSceneScreenshot } from '../../Utils/sceneScreenshot';
import { clickOnlyEvents } from '../../Utils/clickOnlyEvents';
import { addSavedView, captureView, deleteSavedView, fetchSharedViews, fetchViewById, mergeLocalAndSharedViews, nextViewOrder, postSharedView, deleteSharedViewRemote, readCachedSharedViews, readSavedViews } from '../../Utils/savedViews';
import { copyViewLink, readViewIdFromLocation } from '../../Utils/viewUrl';
import { animateCameraTo } from '../../Utils/cameraTransition';
import HelpOverlay from '../ui/HelpOverlay';
import OnboardingOverlay from '../ui/OnboardingOverlay';
import EmptySiteState from '../ui/EmptySiteState';
import ToolStatusChip from '../ui/ToolStatusChip';
import SceneSearchStrip from '../ui/SceneSearchStrip';
import SavedViewsStrip from '../ui/SavedViewsStrip';
import { flyToLayer } from '../../Utils/sceneSearch';
import { useToast } from '../ui/Toast';
import {
  cachedKeysFromLayers,
  layerVisibilityCoversModelList,
  opacityMapFromLayers,
  preserveUnloadedVisibleKeys,
  readCachedKeys,
  readIsolatedKeys,
  readSession,
  savedVisibleIncludes,
  sessionHasVisibleList,
  writeSession,
} from '../../Utils/sessionRestore';
import { objectDisplayName } from '../../Utils/objectIdMatch';
import { readRecentLayers, recordRecentLayer } from '../../Utils/recentLayers';
import { normalizeDockSide, readUnifiedPanelDock, writeUnifiedPanelDock } from '../../Utils/unifiedPanelDock';
import { ensurePanelLayoutVersion } from '../../ui/panelLayout';
import { hasSeenOnboarding, markOnboardingSeen } from '../../Utils/onboarding';

function layerWasIdle(cur) {
  return cur?.loadStatus === 'idle' || cur?.loadStatus === undefined || cur?.loadStatus === 'failed';
}

function applyVisibleKeys(prev, keys) {
  const want = new Set(keys);
  const next = { ...prev };
  Object.keys(next).forEach((key) => {
    const on = want.has(key);
    const cur = next[key];
    next[key] = {
      ...cur,
      visible: on,
      loadStatus: on && layerWasIdle(cur) ? 'loading' : cur.loadStatus,
    };
  });
  return next;
}

function showOneLayer(prev, modelName) {
  const cur = prev[modelName] || { visible: false, loadStatus: 'idle', opacity: 1 };
  return {
          ...prev,
    [modelName]: {
      ...cur,
          visible: true,
      loadStatus: layerWasIdle(cur) ? 'loading' : cur.loadStatus,
    },
  };
}

const Presentation = ({ userEmail, onLogout }) => {
  const { hasPermission, user } = useAuth();
  const site = getSite();
  const PROJECT_NAME = site.projectName || ENV_CONFIG.PROJECT_NAME;
  const canAccessFileManagement = hasPermission('DataviewerManagement', 'View');
  const { modelList: catalogueList, holePresets, loading: catalogueLoading, error: catalogueError, refresh: refreshCatalogue } =
    useModelCatalogue();
  const loadedHoleLayers = useHoleQueryStore((s) => s.loadedLayers);
  const sharedHoleFilters = useHoleQueryStore((s) => s.sharedFilters);
  const hydrateBuiltins = useHoleQueryStore((s) => s.hydrateBuiltins);
  const hydrateRemoteFilters = useHoleQueryStore((s) => s.hydrateRemoteFilters);
  const modelList = useMemo(
    () => mergeHoleQueryLayers(catalogueList, loadedHoleLayers, sharedHoleFilters),
    [catalogueList, loadedHoleLayers, sharedHoleFilters]
  );

  useEffect(() => {
    hydrateBuiltins(holePresets || []);
  }, [holePresets, hydrateBuiltins]);

  useEffect(() => {
    hydrateRemoteFilters(PROJECT_NAME);
  }, [PROJECT_NAME, hydrateRemoteFilters]);

  useEffect(() => {
    hydrateHoleRecordStore();
  }, []);
  const { canInstall, installed: pwaInstalled, swReady, swError, promptInstall } = usePwaInstall();
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const toast = useToast();

  const [three, setThree] = useState(null)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [userMenuPos, setUserMenuPos] = useState(null)
  const [showFileManagement, setShowFileManagement] = useState(false)
  const [showPrepareOffline, setShowPrepareOffline] = useState(false)
  const [offlineProgress, setOfflineProgress] = useState({ done: 0, total: 0 })
  const [offlineBusy, setOfflineBusy] = useState(false)
  const [offlineMessage, setOfflineMessage] = useState('')
  const [updateAvailable, setUpdateAvailable] = useState(null)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [appVersionLabel, setAppVersionLabel] = useState(() => getStoredVersion().appVersion || '…')
  const [offlineReady, setOfflineReadyState] = useState(() => getStoredVersion().offlineReady)
  const offlineAbortRef = useRef(null)
  const userMenuRef = useRef(null)
  const userMenuPanelRef = useRef(null)
  const defaultVisibilitySyncedRef = useRef(false);

  const catalogueModelCount = useMemo(
    () => (modelList || []).reduce((n, g) => n + (g.models?.length || 0), 0),
    [modelList]
  );
  const catalogueEmpty = !catalogueLoading && catalogueModelCount === 0;

  const logoSrc = useMemo(() => {
    const url = site.logoUrl;
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    return `${base}/${String(url).replace(/^\//, '')}`;
  }, [site.logoUrl]);
  
  const { progress } = useProgress()
  const { getLoadingList, getFailedList, getFailedObject, loadingObjects, isFailed, loadingProgress } = useLoadingTracker()

  const [isScribbleMode, setIsScribbleMode] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#eab308');
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const initialSceneLoadCompleteRef = useRef(false);

  const [bootSession] = useState(() => {
    ensurePanelLayoutVersion();
    const session = readSession(PROJECT_NAME);
    if (readViewIdFromLocation()) {
      return session ? { panel: session.panel } : null;
    }
    if (session?.section) useSectionStore.getState().hydrate(session.section);
    return session;
  });
  const [recentLayerKeys, setRecentLayerKeys] = useState(() => readRecentLayers(PROJECT_NAME));
  const [bootPanelDock] = useState(() => readUnifiedPanelDock(PROJECT_NAME));
  const [panelDocked, setPanelDocked] = useState(() =>
    bootSession?.panel?.docked ?? bootPanelDock.docked
  );
  const [panelDockCollapsed, setPanelDockCollapsed] = useState(() =>
    bootSession?.panel?.collapsed ?? bootPanelDock.collapsed
  );
  const [panelDockWidth, setPanelDockWidth] = useState(() =>
    bootSession?.panel?.dockWidth ?? bootPanelDock.width
  );
  const [panelDockSide, setPanelDockSide] = useState(() =>
    normalizeDockSide(bootSession?.panel?.dockSide ?? bootPanelDock.side)
  );
  const initialCameraRef = useRef(null);
  if (!initialCameraRef.current) {
    initialCameraRef.current = {
      position: bootSession?.camera
        ? [bootSession.camera.x, bootSession.camera.y, bootSession.camera.z]
        : site.defaultCamera?.position || [0, 1500, 2000],
      lookAt: bootSession?.target
        ? [bootSession.target.x, bootSession.target.y, bootSession.target.z]
        : site.defaultCamera?.lookAt || [0, 0, 0],
    };
  }
  const cameraPosition = initialCameraRef.current.position;
  const lookAt = initialCameraRef.current.lookAt;
  const sessionRestoredToastRef = useRef(false);
  
  const [layerVisibility, setLayerVisibility] = useState({}); // Manage visibility of each layer
  const [showUnifiedPanel, setShowUnifiedPanel] = useState(() => {
    if (readCompactLayout()) return false;
    return bootSession?.panel?.open ?? true;
  });
  const [activePanel, setActivePanel] = useState(() => bootSession?.panel?.tab || 'layers');
  const [panelWidth, setPanelWidth] = useState(() => bootSession?.panel?.widthPreset || 'default');
  // Remove local showMeasurements state - now using store
  const [sceneBgColor, setSceneBgColor] = useState(() =>
    bootSession && Object.prototype.hasOwnProperty.call(bootSession, 'sceneBgColor')
      ? bootSession.sceneBgColor
      : null
  ); // null = original gradient, else '#rrggbb'
  const [sceneLookId, setSceneLookId] = useState(() =>
    resolveSceneLook(bootSession?.sceneLookId).id
  );
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showLookPicker, setShowLookPicker] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showSceneData, setShowSceneData] = useState(() => readSceneDataPanelOpen());
  const [showHelp, setShowHelp] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showViews, setShowViews] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showGrid, setShowGrid] = useState(() => Boolean(bootSession?.showGrid));
  const [isolatedKeys, setIsolatedKeys] = useState(() => readIsolatedKeys(bootSession));
  const isolatedKeysRef = useRef(isolatedKeys);
  isolatedKeysRef.current = isolatedKeys;
  const isolateStackRef = useRef(bootSession?.isolateSnapshot || null);
  const [savedViews, setSavedViews] = useState(() =>
    mergeLocalAndSharedViews(readSavedViews(), readCachedSharedViews(PROJECT_NAME))
  );
  const hideStackRef = useRef([]);
  const [hideStackLen, setHideStackLen] = useState(0);
  const [submenuLayout, setSubmenuLayout] = useSubmenuLayout();
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const isCompact = useCompactLayout();
  const effectiveSubmenuLayout = isCompact ? 'column' : submenuLayout;

  // Fly-around state management
  const { isFlyAroundActive, setFlyAround, showMeasurements, setShowMeasurements, clickType, objectDetails } = useClickStore(
    useShallow((state) => ({
      isFlyAroundActive: state.isFlyAroundActive,
      setFlyAround: state.setFlyAround,
      showMeasurements: state.showMeasurements,
      setShowMeasurements: state.setShowMeasurements,
      clickType: state.clickType,
      objectDetails: state.objectDetails,
    }))
  );
  const selectedObjectName = objectDisplayName(objectDetails);

  const sectionPanelOpen = useSectionStore((s) => s.panelOpen);
  const sectionEnabled = useSectionStore((s) => s.enabled);

  const closeOtherCompactTools = useCallback((keep) => {
    if (!isCompact) return;
    if (keep !== 'layers') setShowUnifiedPanel(false);
    if (keep !== 'tools') {
      setShowMeasurements(false);
      useSectionStore.getState().setPanelOpen(false);
    }
    if (keep !== 'draw') setIsScribbleMode(false);
    if (keep !== 'find') setShowSearch(false);
    if (keep !== 'views') setShowViews(false);
    if (keep !== 'prefs' && keep !== 'more') {
      setShowPreferences(false);
      setShowBgPicker(false);
      setShowLookPicker(false);
    }
    if (keep !== 'more') setShowMoreSheet(false);
  }, [isCompact, setShowMeasurements]);

  // Log when showMeasurements changes
  useEffect(() => {
    // Track measurements visibility state
  }, [showMeasurements]);

  const handleUndoHide = useCallback(() => {
    const key = hideStackRef.current.pop();
    setHideStackLen(hideStackRef.current.length);
    if (!key) return;
    setLayerVisibility((prev) => {
      const cur = prev[key];
      if (!cur) return prev;
      const wasIdle = cur.loadStatus === 'idle' || cur.loadStatus === undefined || cur.loadStatus === 'failed';
      return {
        ...prev,
        [key]: { ...cur, visible: true, loadStatus: wasIdle ? 'loading' : cur.loadStatus },
      };
    });
    toast.push({ title: 'Layer restored', message: key, tone: 'success', duration: 2200 });
  }, [toast]);

  const handleScreenshot = useCallback(async () => {
    try {
      await downloadSceneScreenshot(three?.gl, {
        filename: `${PROJECT_NAME.replace(/\s+/g, '-').toLowerCase()}-scene.png`,
        camera: three?.camera,
        scene: three?.scene,
      });
      toast.push({ title: 'Screenshot', message: 'PNG saved', tone: 'success', duration: 2200 });
    } catch (e) {
      toast.push({ title: 'Screenshot failed', message: e?.message || 'Scene not ready', tone: 'error' });
    }
  }, [three, PROJECT_NAME, toast]);

  const handleToggleHelp = useCallback(() => {
    setShowHelp((v) => !v);
  }, []);

  const handleToggleSearch = useCallback(() => {
    setShowSearch((v) => {
      const next = !v;
      if (next) closeOtherCompactTools('find');
      return next;
    });
  }, [closeOtherCompactTools]);

  const handleToggleGrid = useCallback(() => {
    setShowGrid((v) => !v);
  }, []);

  const handleToggleSceneData = useCallback(() => {
    setShowSceneData((v) => {
      const next = !v;
      writeSceneDataPanelOpen(next);
      return next;
    });
  }, []);

  const handleCloseSceneData = useCallback(() => {
    setShowSceneData(false);
    writeSceneDataPanelOpen(false);
  }, []);

  const handleToggleScribbleMode = () => {
    const next = !isScribbleMode;
    if (next && isCompact) closeOtherCompactTools('draw');
    setIsScribbleMode(next);
  };

  const toggleLayersPanel = useCallback(() => {
    const next = !showUnifiedPanel;
    if (next) closeOtherCompactTools('layers');
    setShowUnifiedPanel(next);
  }, [showUnifiedPanel, closeOtherCompactTools]);

  const toggleToolsPanel = useCallback(() => {
    const next = !showMeasurements;
    if (next) closeOtherCompactTools('tools');
    setShowMeasurements(next);
  }, [showMeasurements, setShowMeasurements, closeOtherCompactTools]);

  const toggleViewsPanel = useCallback(() => {
    const next = !showViews;
    if (next) closeOtherCompactTools('views');
    setShowViews(next);
  }, [showViews, closeOtherCompactTools]);

  const togglePreferencesPanel = useCallback(() => {
    const next = !showPreferences;
    if (next) closeOtherCompactTools('prefs');
    setShowPreferences(next);
    setShowMoreSheet(false);
  }, [showPreferences, closeOtherCompactTools]);

  const toggleMoreSheet = useCallback(() => {
    const next = !showMoreSheet;
    if (next) closeOtherCompactTools('more');
    setShowMoreSheet(next);
  }, [showMoreSheet, closeOtherCompactTools]);

  useEffect(() => {
    const controls = three?.controlRef?.current;
    if (!controls) return undefined;
    const prev = controls.enabled;
    controls.enabled = !isScribbleMode;
    return () => {
      controls.enabled = prev;
    };
  }, [isScribbleMode, three]);

  useSceneToolShortcuts({
    enabled: true,
    onHelp: handleToggleHelp,
    onUndoHide: handleUndoHide,
    onScreenshot: handleScreenshot,
    onSearch: handleToggleSearch,
    onToggleGrid: handleToggleGrid,
  });

  useEffect(() => {
    const onEsc = () => {
      setShowHelp(false);
      setShowOnboarding(false);
      setShowViews(false);
      setShowSearch(false);
      setShowLookPicker(false);
      setShowBgPicker(false);
      setShowPreferences(false);
      setShowMoreSheet(false);
      setIsScribbleMode(false);
      setFlyAround(false);
    };
    window.addEventListener('mdv-escape', onEsc);
    return () => window.removeEventListener('mdv-escape', onEsc);
  }, [setFlyAround]);

  // Handle keyboard shortcut for toggling unified panel
  useEffect(() => {
    const handleToggleUnifiedPanel = () => {
      toggleLayersPanel();
    };

    window.addEventListener('toggleUnifiedPanel', handleToggleUnifiedPanel);

    return () => {
      window.removeEventListener('toggleUnifiedPanel', handleToggleUnifiedPanel);
    };
  }, [toggleLayersPanel]);

  useEffect(() => {
    setLayerVisibility((prev) => {
      const hadAny = Object.keys(prev).length > 0;
      const session = bootSession || readSession(PROJECT_NAME);
      const hasSavedVisible = sessionHasVisibleList(session);
      const next = {};
      (modelList || []).forEach((group) => {
        (group.models || []).forEach((model) => {
          const old = prev[model.name];
          const fileChanged = modelFileChanged(old, model);
          if (fileChanged) {
            invalidateModelPath(old.url || old.path || model.path, { replaceWithUrl: model.url });
          }
          const restoredVisible = hasSavedVisible && savedVisibleIncludes(session, modelList, model.name);
          const restoredCached = readCachedKeys(session).some(
            (id) => id === model.name || resolveLayerKey(modelList, id) === model.name
          );
          const visible = old?.visible ?? (restoredVisible || !!model.queryAutoLoad);
          const opacity = old?.opacity ?? session?.opacity?.[model.name] ?? 1;
          const wasOpen =
            old?.visible || old?.loadStatus === 'ready' || old?.loadStatus === 'loading';
          next[model.name] = {
            visible,
            opacity,
            url: model.url,
            path: model.path,
            mtime: model.mtime,
            size: model.size,
            diskCached: fileChanged ? false : !!(old?.diskCached || restoredCached),
            cacheRev: fileChanged
              ? `${model.mtime || ''}-${model.size || ''}`
              : old?.cacheRev,
            loadStatus:
              fileChanged && wasOpen
                ? 'loading'
                : old?.loadStatus ?? (visible ? 'loading' : 'idle'),
          };
      });
    });
      // Preserve rename: if event already moved visibility, keys match new names via modelList
      // First catalogue load: apply site.defaultLayers
      if (!hadAny) {
        defaultVisibilitySyncedRef.current = false;
      }
      return next;
    });
  }, [modelList, bootSession, PROJECT_NAME]);

  // Mark layers that already have bytes in Cache Storage so the Cached tab survives reload.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const cachedPaths = await listCachedModelPaths();
      if (cancelled) return;
      setLayerVisibility((prev) => {
        let changed = false;
        const next = { ...prev };
        (modelList || []).forEach((group) => {
          (group.models || []).forEach((model) => {
            const cur = next[model.name];
            if (!cur) return;
            const onDisk = modelCacheUrls(model).some((url) => urlIsInCachedPaths(url, cachedPaths));
            if (!!cur.diskCached === onDisk) return;
            if (!onDisk && cur.loadStatus === 'ready') return;
            next[model.name] = { ...cur, diskCached: onDisk };
            changed = true;
          });
        });
        return changed ? next : prev;
      });
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [modelList]);

  // Apply restored session (or site.defaultLayers) once the file catalogue exists.
  // Hole queries hydrate from localStorage immediately; restoring before list_models
  // would mark the session synced and then overwrite saved GLB visibility with holes-only.
  useLayoutEffect(() => {
    if (defaultVisibilitySyncedRef.current) return;
    if (readViewIdFromLocation()) return;
    if (catalogueLoading) return;
    if (Object.keys(layerVisibility).length === 0 && catalogueModelCount > 0) return;
    if (!layerVisibilityCoversModelList(layerVisibility, modelList)) return;

    defaultVisibilitySyncedRef.current = true;
    const session = bootSession || readSession(PROJECT_NAME);
    const sessionKeys = session?.visible;

    if (Array.isArray(sessionKeys) && sessionKeys.length > 0) {
      setLayerVisibility((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          next[key] = { ...next[key], visible: false };
        });
        sessionKeys.forEach((id) => {
          const name = resolveLayerKey(modelList, id);
          const cur = next[name] || next[id];
          if (!cur) return;
          const key = next[name] ? name : id;
          const wasIdle = cur.loadStatus === 'idle' || cur.loadStatus === undefined || cur.loadStatus === 'failed';
          next[key] = {
            ...cur,
            visible: true,
            opacity: session?.opacity?.[key] ?? session?.opacity?.[id] ?? cur.opacity ?? 1,
            loadStatus: wasIdle ? 'loading' : cur.loadStatus || 'ready',
          };
        });
        if (session?.opacity) {
          Object.keys(session.opacity).forEach((id) => {
            const name = resolveLayerKey(modelList, id);
            const key = next[name] ? name : id;
            if (!next[key]) return;
            next[key] = { ...next[key], opacity: session.opacity[id] };
          });
        }
        return next;
      });
      if (session?.isolatedKey || (Array.isArray(session?.isolatedKeys) && session.isolatedKeys.length)) {
        isolateStackRef.current = session.isolateSnapshot || sessionKeys;
        setIsolatedKeys(readIsolatedKeys(session));
      }
      if (!sessionRestoredToastRef.current) {
        sessionRestoredToastRef.current = true;
        toast.push({
          title: 'Session restored',
          message: 'Last camera, layers, opacity, and panel layout',
          tone: 'success',
          duration: 2600,
        });
      }
      return;
    }

    const visibleObjects = site.defaultLayers || [];
    setLayerVisibility((prev) => {
      const newVisibility = { ...prev };
      Object.keys(newVisibility).forEach((key) => {
        newVisibility[key] = { ...newVisibility[key], visible: false };
      });
      visibleObjects.forEach((id) => {
        const name = resolveLayerKey(modelList, id);
        if (newVisibility[name]) {
          const p = newVisibility[name];
          const wasIdle = p.loadStatus === 'idle' || p.loadStatus === undefined || p.loadStatus === 'failed';
          newVisibility[name] = {
            ...p,
            visible: true,
            loadStatus: wasIdle ? 'loading' : p.loadStatus || 'ready',
          };
        }
      });
      return newVisibility;
    });
  }, [layerVisibility, modelList, site.defaultLayers, PROJECT_NAME, bootSession, toast, catalogueLoading, catalogueModelCount]);

  const saveSessionNow = useCallback(() => {
    if (!defaultVisibilitySyncedRef.current) return;
    const cam = three?.camera?.position;
    const controls = three?.controlRef?.current;
    const target = controls?.target;
    const visibleNow = Object.keys(layerVisibility).filter((k) => layerVisibility[k]?.visible);
    const existing = readSession(PROJECT_NAME);
    const pendingVisible = preserveUnloadedVisibleKeys(existing?.visible, layerVisibility, modelList);
    const visible = [...visibleNow, ...pendingVisible];
    writeSession(PROJECT_NAME, {
      camera: cam
        ? { x: cam.x, y: cam.y, z: cam.z }
        : existing?.camera || null,
      target: target
        ? { x: target.x, y: target.y, z: target.z }
        : existing?.target || null,
      visible: visible.length > 0 ? visible : existing?.visible || visible,
      cached: cachedKeysFromLayers(layerVisibility),
      opacity: opacityMapFromLayers(layerVisibility),
      isolatedKeys,
      isolatedKey: isolatedKeys[0] || null,
      isolateSnapshot: isolateStackRef.current || null,
      sceneBgColor,
      sceneLookId,
      showGrid,
      section: useSectionStore.getState().snapshot(),
      panel: {
        open: showUnifiedPanel,
        tab: activePanel,
        widthPreset: panelWidth,
        docked: panelDocked,
        collapsed: panelDockCollapsed,
        dockWidth: panelDockWidth,
        dockSide: panelDockSide,
      },
    });
  }, [
    three,
    layerVisibility,
    sceneBgColor,
    sceneLookId,
    PROJECT_NAME,
    isolatedKeys,
    showGrid,
    showUnifiedPanel,
    activePanel,
    panelWidth,
    panelDocked,
    panelDockCollapsed,
    panelDockWidth,
    panelDockSide,
    modelList,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(saveSessionNow, 400);
    return () => window.clearTimeout(timer);
  }, [layerVisibility, sceneBgColor, sceneLookId, isolatedKeys, showGrid, showUnifiedPanel, activePanel, panelWidth, panelDocked, panelDockCollapsed, panelDockWidth, panelDockSide, sectionEnabled, sectionPanelOpen, saveSessionNow]);

  useEffect(() => {
    if (!three?.camera) return undefined;
    const id = window.setInterval(saveSessionNow, 1500);
    const persist = () => saveSessionNow();
    window.addEventListener('pagehide', persist);
    window.addEventListener('beforeunload', persist);
    document.addEventListener('visibilitychange', persist);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('pagehide', persist);
      window.removeEventListener('beforeunload', persist);
      document.removeEventListener('visibilitychange', persist);
      persist();
    };
  }, [three, saveSessionNow]);

  useEffect(() => {
    if (catalogueLoading) return;
    window.__mdvCatalogueReady = true;
    window.dispatchEvent(new CustomEvent('mdv-catalogue-ready'));
  }, [catalogueLoading]);

  useEffect(() => {
    if (catalogueLoading) return;
    if (!hasSeenOnboarding(PROJECT_NAME)) {
      setShowOnboarding(true);
    }
  }, [catalogueLoading, PROJECT_NAME]);

  const handleDismissOnboarding = useCallback(() => {
    markOnboardingSeen(PROJECT_NAME);
    setShowOnboarding(false);
  }, [PROJECT_NAME]);

  const prevLoadingSetRef = useRef(new Set());

  const failedList = getFailedList();
  const isModelLoadingName = (name) => !String(name).startsWith('API_');
  const visibleLoadingList = getLoadingList().filter(
    (objectName) => isModelLoadingName(objectName) && !getFailedObject(objectName)?.backgroundRetry
  );
  const modelFailedList = failedList.filter((f) => isModelLoadingName(f.name));
  const trackedObjectCount = visibleLoadingList.length + modelFailedList.length;
  const objectProgressAvg = visibleLoadingList.length
    ? Math.round(
        visibleLoadingList.reduce((sum, name) => {
          const info = loadingProgress.get(name);
          if (!info) return sum;
          if (info.cached || info.phase === 'parsing') return sum + 100;
          return sum + (info.percent || 0);
        }, 0) / visibleLoadingList.length
      )
    : 0;
  const overlayProgress = visibleLoadingList.length ? objectProgressAvg : Math.round(progress);

  // After the first successful scene bootstrap, ignore drei progress dips caused by background retries
  useEffect(() => {
    if (!initialSceneLoadCompleteRef.current && progress >= 100 && visibleLoadingList.length === 0) {
      initialSceneLoadCompleteRef.current = true;
    }
  }, [progress, loadingObjects, failedList, visibleLoadingList.length]);

  const isInitialSceneLoading =
    !initialSceneLoadCompleteRef.current && (progress < 100 || visibleLoadingList.length > 0);
  const hasActiveLoading = visibleLoadingList.length > 0;

  // When a name leaves the global loading tracker, mark that layer ready if it was still "loading"
  // in layerVisibility (covers cache-fast paths where onLoadComplete ordering is flaky).
  useEffect(() => {
    const prevSet = prevLoadingSetRef.current;
    const currSet = loadingObjects;
    prevLoadingSetRef.current = new Set(currSet);

    setLayerVisibility((prevVis) => {
      let changed = false;
      const next = { ...prevVis };
      for (const name of prevSet) {
        if (currSet.has(name)) continue;
        const cur = next[name];
        if (!cur || cur.loadStatus !== 'loading' || !cur.visible) continue;
        if (isFailed(name)) continue;
        next[name] = { ...cur, loadStatus: 'ready', diskCached: true };
        changed = true;
      }
      return changed ? next : prevVis;
    });
  }, [loadingObjects, isFailed]);

  // Scene background: CSS radial behind a transparent WebGL clear (lighter centre, darker edges).
  useEffect(() => {
    const css = sceneBackgroundCss(sceneBgColor);
    document.documentElement.style.setProperty('--gradient-bg', css);
    const stages = document.querySelectorAll('.mdv-scene-stage, .mdv-scene-viewport');
    stages.forEach((el) => {
      el.style.background = css;
    });
    if (three?.scene) three.scene.background = null;
    if (three?.gl?.setClearColor) three.gl.setClearColor(0x000000, 0);
  }, [sceneBgColor, three]);

  const handleLayerLoadComplete = useCallback((modelName) => {
    setLayerVisibility((prev) => {
      if (!prev[modelName]) return prev;
      return {
        ...prev,
        [modelName]: { ...prev[modelName], loadStatus: 'ready', diskCached: true },
      };
    });
  }, []);

  const handleLayerLoadFailed = useCallback((modelName) => {
    setLayerVisibility((prev) => {
      if (!prev[modelName]) return prev;
      return {
        ...prev,
        [modelName]: { ...prev[modelName], loadStatus: 'failed' },
      };
    });
  }, []);

  const handleTogglePanelDock = useCallback(() => {
    setPanelDocked((prev) => {
      const next = !prev;
      if (next) {
        setPanelDockCollapsed(false);
        writeUnifiedPanelDock(PROJECT_NAME, { docked: true, collapsed: false, side: panelDockSide });
      } else {
        writeUnifiedPanelDock(PROJECT_NAME, { docked: false });
      }
      return next;
    });
  }, [PROJECT_NAME, panelDockSide]);

  const handleDockToSide = useCallback(
    (side) => {
      const nextSide = normalizeDockSide(side);
      setPanelDockSide(nextSide);
      setPanelDocked(true);
      setPanelDockCollapsed(false);
      writeUnifiedPanelDock(PROJECT_NAME, { docked: true, collapsed: false, side: nextSide });
    },
    [PROJECT_NAME]
  );

  const handleTogglePanelDockCollapse = useCallback(
    (collapsed) => {
      setPanelDockCollapsed(collapsed);
      writeUnifiedPanelDock(PROJECT_NAME, { collapsed });
    },
    [PROJECT_NAME]
  );

  const handlePanelDockWidthChange = useCallback(
    (width) => {
      setPanelDockWidth(width);
      writeUnifiedPanelDock(PROJECT_NAME, { width });
    },
    [PROJECT_NAME]
  );

  const touchRecentLayer = useCallback(
    (layerKey) => {
      if (!layerKey) return;
      setRecentLayerKeys((prev) => recordRecentLayer(PROJECT_NAME, layerKey, prev));
    },
    [PROJECT_NAME]
  );

  const handleToggleVisibility = useCallback((modelName) => {
    touchRecentLayer(modelName);
    const isolating = isolatedKeysRef.current.length > 0;
    const currentlyVisible = !!layerVisibility[modelName]?.visible;
    const nextVis = !currentlyVisible;

    if (isolating) {
      const nextSet = new Set(isolatedKeysRef.current);
      if (nextVis) {
        nextSet.add(modelName);
        isolatedKeysRef.current = Array.from(nextSet);
        setIsolatedKeys(isolatedKeysRef.current);
        setLayerVisibility((prev) => showOneLayer(prev, modelName));
        return;
      }
      nextSet.delete(modelName);
      if (nextSet.size === 0) {
        const snap = isolateStackRef.current;
        isolateStackRef.current = null;
        isolatedKeysRef.current = [];
        setIsolatedKeys([]);
        if (snap) setLayerVisibility((prev) => applyVisibleKeys(prev, snap));
        return;
      }
      isolatedKeysRef.current = Array.from(nextSet);
      setIsolatedKeys(isolatedKeysRef.current);
      setLayerVisibility((prev) => {
        const cur = prev[modelName];
        if (!cur) return prev;
        return { ...prev, [modelName]: { ...cur, visible: false } };
      });
      return;
    }

    setLayerVisibility((prevState) => {
      const cur = prevState[modelName] || { visible: false, loadStatus: 'idle', opacity: 1 };
      const on = !cur.visible;
      if (on && layerWasIdle(cur)) {
        return { ...prevState, [modelName]: { ...cur, visible: true, loadStatus: 'loading' } };
      }
      return { ...prevState, [modelName]: { ...cur, visible: on } };
    });
  }, [touchRecentLayer, layerVisibility]);

  const handleOpacityChange = useCallback((modelName, opacity) => {
    touchRecentLayer(modelName);
    setLayerVisibility((prev) => {
      const cur = prev[modelName];
      if (!cur) return prev;
      return { ...prev, [modelName]: { ...cur, opacity } };
    });
  }, [touchRecentLayer]);

  const handleIsolate = useCallback((modelName) => {
    touchRecentLayer(modelName);
    const current = new Set(isolatedKeysRef.current);

    if (current.has(modelName)) {
      current.delete(modelName);
      if (current.size === 0) {
        const snap = isolateStackRef.current;
        isolateStackRef.current = null;
        isolatedKeysRef.current = [];
        setIsolatedKeys([]);
        if (snap) setLayerVisibility((prev) => applyVisibleKeys(prev, snap));
        return;
      }
      isolatedKeysRef.current = Array.from(current);
      setIsolatedKeys(isolatedKeysRef.current);
      setLayerVisibility((prev) => {
        const cur = prev[modelName];
        if (!cur) return prev;
        return { ...prev, [modelName]: { ...cur, visible: false } };
      });
      return;
    }

    if (current.size === 0) {
      setLayerVisibility((prev) => {
        isolateStackRef.current = Object.keys(prev).filter((k) => prev[k]?.visible);
        return applyVisibleKeys(prev, [modelName]);
      });
    } else {
      setLayerVisibility((prev) => showOneLayer(prev, modelName));
    }
    current.add(modelName);
    isolatedKeysRef.current = Array.from(current);
    setIsolatedKeys(isolatedKeysRef.current);
  }, [touchRecentLayer]);

  const handleFlyToSearch = useCallback(
    async (item) => {
      const key = item?.layerKey;
      if (!key) return;
      touchRecentLayer(key);
      setShowSearch(false);
      setLayerVisibility((prev) => {
        const cur = prev[key];
        if (!cur) return prev;
        if (cur.visible) return prev;
        const wasIdle = cur.loadStatus === 'idle' || cur.loadStatus === undefined || cur.loadStatus === 'failed';
        return {
          ...prev,
          [key]: { ...cur, visible: true, loadStatus: wasIdle ? 'loading' : cur.loadStatus },
        };
      });
      toast.push({ title: 'Find', message: item.label, tone: 'success', duration: 1800 });
      if (!three?.camera || !three?.scene) {
        toast.push({ title: 'Find', message: 'Scene is not ready', tone: 'warn' });
        return;
      }
      const ok = await flyToLayer({
        scene: three.scene,
        camera: three.camera,
        controls: three.controlRef?.current,
        layerKey: key,
      });
      if (!ok) {
        toast.push({
          title: 'Find',
          message: 'Waiting for that layer to load — try again in a moment',
          tone: 'warn',
        });
      }
    },
    [three, toast, touchRecentLayer]
  );

  const handleOpenStats = useCallback(() => {
    setShowUnifiedPanel(true);
    setActivePanel('statistics');
    setPanelDockCollapsed(false);
    writeUnifiedPanelDock(PROJECT_NAME, { collapsed: false });
  }, [PROJECT_NAME]);

  useEffect(() => {
    if (clickType !== 'info' || !selectedObjectName) return;
    setShowUnifiedPanel(true);
    setActivePanel('information');
    setPanelDockCollapsed(false);
  }, [clickType, selectedObjectName]);

  const hideLayerKey = useClickStore((s) => s.hideLayerKey);
  const clearHideLayerKey = useClickStore((s) => s.clearHideLayerKey);

  // Hide tool (H): click object → turn that layer off in the layers list
  useEffect(() => {
    if (!hideLayerKey) return;
    setLayerVisibility((prev) => {
      const cur = prev[hideLayerKey];
      if (!cur?.visible) return prev;
      hideStackRef.current = [...hideStackRef.current, hideLayerKey].slice(-25);
      setHideStackLen(hideStackRef.current.length);
      return {
        ...prev,
        [hideLayerKey]: { ...cur, visible: false },
      };
    });
    clearHideLayerKey();
  }, [hideLayerKey, clearHideLayerKey]);

  const handleSaveView = useCallback(
    (name) => {
      const cam = three?.camera?.position;
      const target = three?.controlRef?.current?.target;
      if (!cam) {
        toast.push({ title: 'Views', message: 'Scene camera is not ready', tone: 'warn' });
        return;
      }
      const view = captureView({
        name: name || undefined,
        camera: cam,
        target,
        layerVisibility,
        isolatedKeys,
        sceneBgColor,
        sceneLookId,
        showGrid,
        section: useSectionStore.getState().snapshot(),
        order: nextViewOrder(savedViews),
      });
      setSavedViews(addSavedView(view));
      toast.push({ title: 'View saved', message: view.name, tone: 'success', duration: 2200 });
    },
    [three, layerVisibility, isolatedKeys, sceneBgColor, sceneLookId, showGrid, savedViews, toast]
  );

  const handleShareView = useCallback(
    async (name) => {
      const cam = three?.camera?.position;
      const target = three?.controlRef?.current?.target;
      if (!cam) {
        toast.push({ title: 'Views', message: 'Scene camera is not ready', tone: 'warn' });
        return;
      }
      const view = captureView({
        name: name || undefined,
        camera: cam,
        target,
        layerVisibility,
        isolatedKeys,
        sceneBgColor,
        sceneLookId,
        showGrid,
        section: useSectionStore.getState().snapshot(),
        order: nextViewOrder(savedViews),
      });
      setSavedViews(addSavedView(view));
      try {
        await postSharedView(PROJECT_NAME, view);
        const shared = await fetchSharedViews(PROJECT_NAME);
        setSavedViews(mergeLocalAndSharedViews(readSavedViews(), shared));
        try {
          await copyViewLink(view.id);
          toast.push({ title: 'View shared', message: `${view.name} — link copied`, tone: 'success', duration: 3200 });
        } catch {
          toast.push({ title: 'View shared', message: `${view.name} — visible to everyone on this site`, tone: 'success', duration: 2800 });
        }
      } catch (e) {
        toast.push({
          title: 'Saved locally',
          message: e?.message || 'Could not share — kept on this browser',
          tone: 'warn',
        });
      }
    },
    [three, layerVisibility, isolatedKeys, sceneBgColor, sceneLookId, showGrid, savedViews, PROJECT_NAME, toast]
  );

  const handleApplyView = useCallback(
    (view) => {
      if (!view) return;
      setLayerVisibility((prev) => {
        const next = { ...prev };
        const want = new Set();
        (view.visible || []).forEach((id) => {
          const name = resolveLayerKey(modelList, id);
          want.add(next[name] ? name : id);
        });
        Object.keys(next).forEach((key) => {
          const on = want.has(key);
          const cur = next[key];
          const wasIdle = cur.loadStatus === 'idle' || cur.loadStatus === undefined || cur.loadStatus === 'failed';
          const resolvedId = (view.visible || []).find((id) => resolveLayerKey(modelList, id) === key || id === key);
          const opacity = view.opacity?.[key] ?? (resolvedId ? view.opacity?.[resolvedId] : undefined) ?? cur.opacity ?? 1;
          next[key] = {
            ...cur,
            visible: on,
            opacity,
            loadStatus: on && wasIdle ? 'loading' : cur.loadStatus,
          };
        });
        return next;
      });
      if (view.camera && three?.camera) {
        animateCameraTo({
          camera: three.camera,
          controls: three.controlRef?.current,
          position: view.camera,
          target: view.target,
        });
      }
      if (Object.prototype.hasOwnProperty.call(view, 'sceneBgColor')) {
        setSceneBgColor(view.sceneBgColor);
      }
      if (view.sceneLookId) {
        setSceneLookId(resolveSceneLook(view.sceneLookId).id);
      }
      if (Object.prototype.hasOwnProperty.call(view, 'showGrid')) {
        setShowGrid(Boolean(view.showGrid));
      }
      if (view.section) {
        useSectionStore.getState().hydrate(view.section);
      }
      setIsolatedKeys(readIsolatedKeys(view));
      setFlyAround(false);
    },
    [three, setFlyAround, modelList]
  );

  const appliedUrlViewRef = useRef('');
  useEffect(() => {
    const id = readViewIdFromLocation();
    if (!id || appliedUrlViewRef.current === id) return;
    if (!three || catalogueLoading) return;
    if (!layerVisibilityCoversModelList(layerVisibility, modelList) && catalogueModelCount > 0) return;
    let cancelled = false;
    (async () => {
      const shared = await fetchSharedViews(PROJECT_NAME).catch(() => []);
      if (cancelled) return;
      const merged = mergeLocalAndSharedViews(readSavedViews(), shared);
      setSavedViews(merged);
      let view = merged.find((v) => v.id === id) || (await fetchViewById(PROJECT_NAME, id));
      if (cancelled) return;
      appliedUrlViewRef.current = id;
      defaultVisibilitySyncedRef.current = true;
      if (!view) {
        toast.push({ title: 'View not found', message: 'This link has no saved view on this site', tone: 'warn' });
        return;
      }
      handleApplyView(view);
      toast.push({ title: 'Opened view', message: view.name, tone: 'success', duration: 2800 });
    })();
    return () => {
      cancelled = true;
    };
  }, [three, catalogueLoading, catalogueModelCount, layerVisibility, modelList, PROJECT_NAME, handleApplyView, toast]);

  const handleCopyViewLink = useCallback(
    async (view) => {
      if (!view?.id) return;
      try {
        await copyViewLink(view.id);
        toast.push({ title: 'Link copied', message: view.name || view.id, tone: 'success', duration: 2200 });
      } catch (e) {
        toast.push({ title: 'Could not copy link', message: e?.message || '', tone: 'warn' });
      }
    },
    [toast]
  );

  const handleDeleteView = useCallback(
    async (viewOrId) => {
      const view = typeof viewOrId === 'object' && viewOrId ? viewOrId : { id: viewOrId };
      if (!view.id) return;
      setSavedViews(deleteSavedView(view.id));
      if (view.shared) {
        try {
          await deleteSharedViewRemote(PROJECT_NAME, view.id);
          const shared = await fetchSharedViews(PROJECT_NAME);
          setSavedViews(mergeLocalAndSharedViews(readSavedViews(), shared));
        } catch (e) {
          const shared = await fetchSharedViews(PROJECT_NAME).catch(() => []);
          setSavedViews(mergeLocalAndSharedViews(readSavedViews(), shared));
          toast.push({ title: 'Delete failed', message: e?.message || 'Could not remove shared view', tone: 'error' });
        }
      }
    },
    [PROJECT_NAME, toast]
  );

  useEffect(() => {
    let cancelled = false;
    fetchSharedViews(PROJECT_NAME)
      .then((shared) => {
        if (cancelled) return;
        setSavedViews(mergeLocalAndSharedViews(readSavedViews(), shared));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [PROJECT_NAME]);

  // Get first initial of user email
  const getUserInitial = () => {
    const display = user?.name || userEmail;
    if (!display) return 'U';
    return display.charAt(0).toUpperCase();
  };

  const closeUserMenu = useCallback(() => {
    setShowUserMenu(false);
    setUserMenuPos(null);
  }, []);

  const placeUserMenu = useCallback(() => {
    if (!userMenuRef.current) return;
    const btn = userMenuRef.current.querySelector('[aria-haspopup="menu"]');
    const r = (btn || userMenuRef.current).getBoundingClientRect();
    const menuWidth = Math.min(280, Math.max(200, window.innerWidth - 16));
    const gap = 8;
    setUserMenuPos({
      top: r.bottom + gap,
      left: Math.min(Math.max(8, r.right - menuWidth), window.innerWidth - menuWidth - 8),
    });
  }, []);

  const handleUserMenuToggle = () => {
    setShowUserMenu((open) => {
      const next = !open;
      if (next) placeUserMenu();
      else setUserMenuPos(null);
      return next;
    });
  };

  // Close user menu when clicking outside (avatar + portaled panel)
  useEffect(() => {
    if (!showUserMenu) return;

    const handleClickOutside = (event) => {
      const t = event.target;
      if (userMenuRef.current?.contains(t)) return;
      if (userMenuPanelRef.current?.contains(t)) return;
      closeUserMenu();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu, closeUserMenu]);

  useEffect(() => {
    if (!showUserMenu) return;
    placeUserMenu();
  }, [showUserMenu, placeUserMenu]);

  const handleFileManagementClick = () => {
    closeUserMenu();
    setShowLoadingOverlay(false);
    setShowFileManagement(true);
  };

  const runPrepareOffline = async () => {
    if (offlineBusy || offlineAbortRef.current) return;
    const modelUrls = collectPrecacheUrls(modelList);
    const decoderUrls = decoderAssetUrls();
    const urls = [...decoderUrls, ...modelUrls];
    if (!modelUrls.length) {
      setOfflineMessage('No model URLs to cache yet.');
      return;
    }
    setOfflineBusy(true);
    setOfflineMessage('Caching Draco decoder and models — you can close this and keep working.');
    setOfflineProgress({ done: 0, total: urls.length });
    const ac = new AbortController();
    offlineAbortRef.current = ac;
    try {
      await precacheAppAssets(decoderUrls, {
        signal: ac.signal,
        onProgress: ({ done }) => {
          setOfflineProgress({ done, total: urls.length });
        },
      });
      await precacheUrls(modelUrls, {
        concurrency: 2,
        signal: ac.signal,
        onProgress: ({ done, total, url }) => {
          setOfflineProgress({ done: decoderUrls.length + done, total: urls.length, url });
        },
      });
      try {
        const { preloadDracoDecoder } = await import('../../loaders/LoadModel');
        preloadDracoDecoder?.();
      } catch {
        /* decoder warm-up is optional */
      }
      const urlSet = new Set(modelUrls);
      setLayerVisibility((prev) => {
        const next = { ...prev };
        (modelList || []).forEach((group) => {
          (group.models || []).forEach((model) => {
            const cur = next[model.name];
            if (!cur || cur.diskCached) return;
            if (modelCacheUrls(model).some((url) => urlSet.has(url))) {
              next[model.name] = { ...cur, diskCached: true };
            }
          });
        });
        return next;
      });
      setOfflineReady(true);
      setOfflineReadyState(true);
      setOfflineMessage('Offline pack ready. Models will load without internet.');
    } catch (e) {
      if (e?.name === 'AbortError' || e?.message === 'cancelled') {
        setOfflineMessage('Cancelled.');
      } else {
        setOfflineMessage(e?.message || 'Prepare Offline failed');
      }
    } finally {
      setOfflineBusy(false);
      offlineAbortRef.current = null;
    }
  };

  const cancelPrepareOffline = () => {
    offlineAbortRef.current?.abort();
  };

  // Version check + first-time wipe
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const remote = await fetchRemoteVersion();
        if (cancelled) return;
        const stored = getStoredVersion();
        if (needsFirstTimeWipe(stored, remote)) {
          await performFirstTimeWipe(remote);
          setAppVersionLabel(remote.appVersion || '1.0.0');
          setOfflineReadyState(false);
          setOfflineMessage('Offline storage reset for MineDynamicsViewer 1.0.0. Use Prepare Offline to download models.');
          return;
        }
        setAppVersionLabel(stored.appVersion || remote.appVersion);
        setOfflineReadyState(!!stored.offlineReady);
        if (isRemoteNewer(stored, remote)) {
          setUpdateAvailable(remote);
        } else {
          setUpdateAvailable(null);
        }
      } catch (e) {
        console.warn('[version] check failed', e);
      }
    };
    check();
    const id = setInterval(check, 45 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Invalidate RAM + Cache Storage when a model file changes; keep open layers open
  useEffect(() => {
    const onChanged = (evt) => {
      const detail = evt.detail || {};
      const path = detail.path;
      const url = detail.url;
      const from = detail.from;
      const deleted = detail.deleted;

      const bumpLayer = (prev, key, extra = {}) => {
        const cur = prev[key];
        if (!cur) return prev;
        const wasOpen = cur.visible || cur.loadStatus === 'ready' || cur.loadStatus === 'loading';
        return {
          ...prev,
          [key]: {
            ...cur,
            ...extra,
            diskCached: false,
            loadStatus: wasOpen ? 'loading' : cur.loadStatus,
            cacheRev: `${extra.mtime ?? cur.mtime ?? ''}-${extra.size ?? cur.size ?? ''}-${Date.now()}`,
          },
        };
      };

      (async () => {
        if (from && path && !deleted) {
          setLayerVisibility((prev) => {
            if (!prev[from]) return prev;
            const next = { ...prev };
            next[path] = {
              ...next[from],
              url: url || next[from].url,
              path,
              mtime: detail.mtime ?? next[from].mtime,
              size: detail.size ?? next[from].size,
              loadStatus: next[from].visible || next[from].loadStatus === 'ready' ? 'loading' : next[from].loadStatus,
              diskCached: false,
              cacheRev: `${detail.mtime ?? ''}-${detail.size ?? ''}`,
            };
            delete next[from];
            return next;
          });
          await invalidateModelPath(from);
        }

        if (path) await invalidateModelPath(path, { replaceWithUrl: url });
        else if (url) await invalidateModelPath(url, { replaceWithUrl: url });

        if (path && !deleted) {
          setLayerVisibility((prev) => {
            const key =
              prev[path] ? path : Object.keys(prev).find((k) => prev[k]?.path === path);
            if (!key) return prev;
            return bumpLayer(prev, key, {
              url: url || prev[key].url,
              path,
              mtime: detail.mtime,
              size: detail.size,
            });
          });
        }

        if (detail.reason !== 'disk-fingerprint') {
          refreshCatalogue({ silent: true });
        }
      })();
    };
    window.addEventListener('models-changed', onChanged);
    return () => window.removeEventListener('models-changed', onChanged);
  }, [refreshCatalogue]);

  // Handle logout confirmation
  const handleLogoutClick = () => {
    closeUserMenu();
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    onLogout();
    setShowLogoutConfirm(false);
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <div className={`flex flex-col h-full min-h-0 overflow-hidden relative bg-black text-zinc-100 mdv-app-shell ${isCompact ? 'mdv-compact-app' : ''}`}>
      {/* Slim top bar with horizontal tools */}
      <header
        className={`flex-shrink-0 bg-black/90 border-b border-yellow-600/25 flex items-center gap-2 z-[60] backdrop-blur-sm ${
          isCompact ? 'mdv-mobile-header px-3' : 'h-9 gap-3 px-3'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0 shrink">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt=""
              className={`w-auto object-contain flex-shrink-0 ${isCompact ? 'h-7 max-w-[5rem]' : 'h-6 max-w-[4.5rem]'}`}
            />
          ) : null}
          <span
            className={`font-semibold uppercase tracking-[0.22em] text-yellow-500/70 flex-shrink-0 ${
              isCompact ? 'text-[10px]' : 'text-[9px]'
            }`}
            style={{ fontFamily: 'var(--mdv-display)' }}
          >
            {ENV_CONFIG.PRODUCT_NAME}
          </span>
          <span className="h-3 w-px bg-yellow-600/30 hidden lg:block" />
          <h1
            className={`font-semibold text-yellow-400/95 truncate tracking-wide ${
              isCompact ? 'block text-[12px] max-w-[40vw]' : 'text-[13px] hidden sm:block max-w-[10rem] lg:max-w-[14rem]'
            }`}
            style={{ fontFamily: 'var(--mdv-display)' }}
          >
            {PROJECT_NAME}
          </h1>
        </div>

        {!isCompact ? (
        <div className="flex-1 min-w-0 flex justify-center">
          <SceneTopbarTools
            isScribbleMode={isScribbleMode}
            onToggleScribble={handleToggleScribbleMode}
            showPanel={showUnifiedPanel}
            onTogglePanel={toggleLayersPanel}
            showMeasurements={showMeasurements}
            onToggleMeasurements={toggleToolsPanel}
            loadingCount={visibleLoadingList.length}
            onShowLoading={() => setShowLoadingOverlay(true)}
            showViews={showViews}
            onToggleViews={toggleViewsPanel}
            showSearch={showSearch}
            onToggleSearch={handleToggleSearch}
            onScreenshot={handleScreenshot}
            showPreferences={showPreferences}
            onTogglePreferences={togglePreferencesPanel}
          />
        </div>
        ) : (
          <div className="flex-1" />
        )}

        <div className="flex items-center gap-1.5 shrink-0" ref={userMenuRef}>
          <span className="text-[9px] uppercase tracking-wider text-zinc-600 hidden md:inline">
            v{appVersionLabel || '…'}
          </span>
          <button
            type="button"
            onClick={handleUserMenuToggle}
            className={`m-0 bg-yellow-500 text-black rounded-full hover:bg-yellow-400 focus:outline-none font-bold flex items-center justify-center border border-yellow-300/40 ${
              isCompact ? 'w-9 h-9 text-[13px]' : 'w-7 h-7 text-[11px]'
            }`}
            title={user?.name || userEmail || 'Account'}
            aria-haspopup="menu"
            aria-expanded={showUserMenu}
            aria-label="Account menu"
          >
            {getUserInitial()}
          </button>
          {showUserMenu &&
            userMenuPos &&
            createPortal(
              <div
                ref={userMenuPanelRef}
                role="menu"
                className="fixed z-[300] min-w-[12.5rem] max-w-[16rem] rounded-md border border-yellow-600/35 bg-zinc-950 shadow-2xl overflow-hidden"
                style={{ top: userMenuPos.top, left: userMenuPos.left }}
              >
                <div className="px-3 py-2 border-b border-yellow-600/20">
                  <p className="text-[9px] uppercase tracking-[0.16em] text-yellow-500/80">Signed in</p>
                  <p className="text-xs text-zinc-100 truncate mt-0.5" title={user?.name || userEmail}>
                    {user?.name || userEmail || 'User'}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-1">
                    v{appVersionLabel || '…'}
                    {offlineBusy
                      ? ` · Caching ${offlineProgress.done}/${offlineProgress.total || '…'}`
                      : offlineReady
                        ? ' · Offline ready'
                        : ''}
                    {catalogueLoading ? ' · Loading layers…' : ''}
                  </p>
                </div>
                {updateAvailable && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { closeUserMenu(); setShowUpdateModal(true); }}
                    className="w-full text-left px-3 py-2 text-xs text-yellow-400 hover:bg-zinc-900 transition-colors"
                  >
                    Update available
                  </button>
                )}
                {canAccessFileManagement && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleFileManagementClick}
                    className="w-full text-left px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900 transition-colors"
                  >
                    File Management
                  </button>
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { closeUserMenu(); setShowPrepareOffline(true); }}
                  className="w-full text-left px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900 transition-colors"
                >
                  {offlineBusy ? 'Prepare Offline (caching…)' : 'Prepare Offline'}
                </button>
                {!pwaInstalled && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={async () => {
                      closeUserMenu();
                      if (canInstall) {
                        const result = await promptInstall();
                        if (!result.ok) setShowInstallHelp(true);
                      } else {
                        setShowInstallHelp(true);
                      }
                    }}
                    className="w-full text-left px-3 py-2 text-xs text-yellow-400 hover:bg-zinc-900 transition-colors"
                  >
                    {canInstall ? 'Install app' : 'Install app (help)'}
                  </button>
                )}
                {pwaInstalled && (
                  <p className="px-3 py-2 text-[10px] text-zinc-500 border-t border-zinc-800">
                    App installed — open from Start / Chrome apps for offline
                  </p>
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogoutClick}
                  className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-zinc-900 transition-colors border-t border-zinc-800"
                >
                  Logout
                </button>
              </div>,
              document.body
            )}
        </div>
      </header>

      <div className="flex flex-1 min-h-0 flex-row relative">
      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[120] backdrop-blur-[2px]">
          <div className="bg-zinc-950 border border-yellow-600/40 rounded-md p-5 max-w-sm mx-4 shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-full">
            <div className="flex items-center mb-4">
              <div className="w-9 h-9 bg-yellow-500 rounded-full flex items-center justify-center mr-3 text-black font-bold text-sm">
                {getUserInitial()}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-yellow-400 tracking-wide">Logout</h3>
                <p className="text-xs text-zinc-400 mt-0.5">End this session?</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleLogoutCancel}
                className="px-3 py-1.5 text-xs text-zinc-300 border border-zinc-700 rounded hover:border-zinc-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="px-3 py-1.5 text-xs bg-yellow-500 text-black font-semibold rounded hover:bg-yellow-400 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpdateModal && updateAvailable && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[120] backdrop-blur-[2px]">
          <div className="bg-zinc-950 border border-yellow-600/40 rounded-md p-5 max-w-md mx-4 shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-full">
            <h3 className="text-sm font-semibold text-yellow-400 mb-1 tracking-wide">Update available</h3>
            <p className="text-xs text-zinc-400 mb-4 leading-relaxed">
              Version {updateAvailable.appVersion} is ready. Updating replaces the cached app and
              clears cached models — run Prepare Offline again after.
            </p>
            <div className="flex justify-end gap-2">
          <button
                type="button"
                className="px-3 py-1.5 text-xs text-zinc-300 border border-zinc-700 rounded hover:border-zinc-500"
                onClick={() => setShowUpdateModal(false)}
              >
                Later
          </button>
          <button
                type="button"
                className="px-3 py-1.5 text-xs bg-yellow-500 text-black rounded font-semibold hover:bg-yellow-400"
                onClick={() => activateAppUpdate(updateAvailable)}
              >
                Update now
          </button>
        </div>
                </div>
          </div>
      )}

      {showPrepareOffline && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[120] backdrop-blur-[2px]">
          <div className="bg-zinc-950 border border-yellow-600/40 rounded-md p-5 max-w-md mx-4 shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-full">
            <h3 className="text-sm font-semibold text-yellow-400 mb-1 tracking-wide">Prepare Offline</h3>
            <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
              Cache models in this browser for offline use. Large downloads may take a while.
              Closing this dialog does not stop caching.
            </p>
            {offlineBusy && (
              <div className="mb-3">
                <div className="h-1 bg-zinc-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-yellow-500 transition-all"
                    style={{
                      width: `${offlineProgress.total ? (100 * offlineProgress.done) / offlineProgress.total : 0}%`,
                    }}
                  />
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5 tracking-wide">
                  {offlineProgress.done} / {offlineProgress.total}
                </p>
              </div>
            )}
            {offlineMessage && <p className="text-xs text-zinc-300 mb-3">{offlineMessage}</p>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-xs text-zinc-300 border border-zinc-700 rounded hover:border-zinc-500"
                onClick={() => setShowPrepareOffline(false)}
              >
                Close
              </button>
              {offlineBusy ? (
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs text-red-300 border border-red-900/60 rounded hover:border-red-500/60"
                  onClick={cancelPrepareOffline}
                >
                  Cancel
                </button>
              ) : (
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs bg-yellow-500 text-black rounded font-semibold hover:bg-yellow-400"
                  onClick={runPrepareOffline}
                >
                  Start
                </button>
        )}
      </div>
                  </div>
                </div>
      )}

      {offlineBusy && !showPrepareOffline && (
        <button
          type="button"
          className="fixed bottom-4 right-4 z-[110] max-w-xs text-left px-3 py-2 rounded-md border border-yellow-600/40 bg-zinc-950/95 shadow-lg hover:border-yellow-500/60"
          onClick={() => setShowPrepareOffline(true)}
          title="Open Prepare Offline"
        >
          <p className="text-[10px] uppercase tracking-[0.14em] text-yellow-500/90">Offline cache</p>
          <div className="h-1 bg-zinc-800 rounded overflow-hidden mt-1.5">
            <div
              className="h-full bg-yellow-500 transition-all"
              style={{
                width: `${offlineProgress.total ? (100 * offlineProgress.done) / offlineProgress.total : 0}%`,
              }}
            />
                  </div>
          <p className="text-[10px] text-zinc-400 mt-1">
            {offlineProgress.done} / {offlineProgress.total} — tap to open
          </p>
        </button>
      )}

      {showInstallHelp && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[120] backdrop-blur-[2px]">
          <div className="bg-zinc-950 border border-yellow-600/40 rounded-md p-5 max-w-md mx-4 shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-full">
            <h3 className="text-sm font-semibold text-yellow-400 mb-2 tracking-wide">Install app</h3>
            <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
              Chrome shows an install icon in the address bar when the page is HTTPS, the service worker
              is running, and the manifest icons load. Also try
              <span className="text-zinc-300"> ⋮ → Cast, save and share → Install page…</span>
            </p>
            <ul className="text-xs text-zinc-300 space-y-1.5 mb-4 list-disc pl-4">
              <li>Service worker: {swReady ? 'active' : 'not active'}</li>
              {swError ? <li className="text-amber-400/90">{swError}</li> : null}
              <li>If the certificate warning appears, click Advanced → Proceed, then hard-refresh (Ctrl+Shift+R).</li>
              <li>After install: run Prepare Offline once, then open the app from the Start menu when DNS fails.</li>
            </ul>
            <div className="flex justify-end gap-2">
              {canInstall && (
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs bg-yellow-500 text-black rounded font-semibold hover:bg-yellow-400"
                  onClick={async () => {
                    await promptInstall();
                    setShowInstallHelp(false);
                  }}
                >
                  Install now
                </button>
              )}
              <button
                type="button"
                className="px-3 py-1.5 text-xs text-zinc-300 border border-zinc-700 rounded hover:border-zinc-500"
                onClick={() => setShowInstallHelp(false)}
              >
                Close
              </button>
                </div>
                  </div>
                </div>
      )}

      {showUnifiedPanel && panelDocked && !isCompact && (
        <UnifiedPanel
          layoutMode="dock"
          modelList={modelList}
          layerVisibility={layerVisibility}
          onToggleVisibility={handleToggleVisibility}
          onIsolate={handleIsolate}
          onOpacityChange={handleOpacityChange}
          isolatedKeys={isolatedKeys}
          onRefreshCatalogue={() => refreshCatalogue()}
          catalogueLoading={catalogueLoading}
          activePanel={activePanel}
          onPanelChange={setActivePanel}
          onClose={() => setShowUnifiedPanel(false)}
          panelWidth={panelWidth}
          onWidthChange={setPanelWidth}
          projectName={PROJECT_NAME}
          recentLayerKeys={recentLayerKeys}
          dockCollapsed={panelDockCollapsed}
          dockWidth={panelDockWidth}
          dockSide={panelDockSide}
          onToggleDock={handleTogglePanelDock}
          onToggleDockCollapse={handleTogglePanelDockCollapse}
          onDockWidthChange={handlePanelDockWidthChange}
          onDockToSide={handleDockToSide}
          selectedObjectName={selectedObjectName}
          onOpenStats={handleOpenStats}
        />
      )}

      {/* Main Content — radial sits here so the absolute Canvas can show through */}
      <div className="flex flex-1 min-h-0 min-w-0 flex-col relative overflow-hidden mdv-scene-viewport" style={{ background: sceneBackgroundCss(sceneBgColor) }}>
      <SubmenuRail
        layout={effectiveSubmenuLayout}
        compact={isCompact}
        hasContent={showMeasurements || isScribbleMode || sectionPanelOpen || showBgPicker || showLookPicker || showViews || showSearch || showPreferences}
      >
        <Measurements
          showMeasurements={showMeasurements}
          layout={effectiveSubmenuLayout}
          onClose={() => setShowMeasurements(false)}
          showHelp={showHelp}
          onHelp={handleToggleHelp}
          flyAround={isFlyAroundActive}
          onToggleFlyAround={() => setFlyAround(!isFlyAroundActive)}
          showGrid={showGrid}
          onToggleGrid={handleToggleGrid}
        />
        <div id="mdv-draw-submenu-slot" className={isScribbleMode ? '' : 'hidden'} />
        <SceneSlicer
          three={three}
          layout={effectiveSubmenuLayout}
          onClose={() => useSectionStore.getState().setPanelOpen(false)}
        />
        <PreferencesStrip
          open={showPreferences}
          layout={effectiveSubmenuLayout}
          onClose={() => setShowPreferences(false)}
          showBgPicker={showBgPicker}
          onToggleBgPicker={() => setShowBgPicker((v) => !v)}
          sceneBgColor={sceneBgColor}
          showLookPicker={showLookPicker}
          onToggleLookPicker={() => setShowLookPicker((v) => !v)}
          submenuLayout={submenuLayout}
          onToggleSubmenuLayout={() =>
            setSubmenuLayout((prev) => (prev === 'column' ? 'row' : 'column'))
          }
          hideLayoutToggle={isCompact}
          showSceneData={showSceneData}
          onToggleSceneData={handleToggleSceneData}
        />
        <SceneBackgroundStrip
          open={showBgPicker}
          layout={effectiveSubmenuLayout}
          sceneBgColor={sceneBgColor}
          onSceneBgChange={setSceneBgColor}
          onClose={() => setShowBgPicker(false)}
        />
        <SceneLookStrip
          open={showLookPicker}
          layout={effectiveSubmenuLayout}
          sceneLookId={sceneLookId}
          onSceneLookChange={(id) => setSceneLookId(resolveSceneLook(id).id)}
          onClose={() => setShowLookPicker(false)}
        />
        <SavedViewsStrip
          open={showViews}
          layout={effectiveSubmenuLayout}
          views={savedViews}
          onClose={() => setShowViews(false)}
          onSave={handleSaveView}
          onShare={handleShareView}
          onApply={handleApplyView}
          onCopyLink={handleCopyViewLink}
          onDelete={handleDeleteView}
        />
        <SceneSearchStrip
          open={showSearch}
          layout={effectiveSubmenuLayout}
          modelList={modelList}
          onClose={() => setShowSearch(false)}
          onSelect={handleFlyToSearch}
        />
      </SubmenuRail>
      <div className={isCompact ? 'relative flex-1 min-h-0 min-w-0 order-first' : 'contents'}>
      <Scribble
        isScribbleMode={isScribbleMode}
        selectedColor={selectedColor}
        onColorChange={setSelectedColor}
        layout={effectiveSubmenuLayout}
        onClose={() => setIsScribbleMode(false)}
      />
      {/* Loading status: icon + count → compact modal */}
      {(hasActiveLoading || modelFailedList.length > 0 || isInitialSceneLoading) &&
        !showLoadingOverlay && (
          <button
            type="button"
            className="absolute top-2 left-1/2 z-[1005] -translate-x-1/2 flex h-9 w-9 items-center justify-center rounded-full border border-yellow-600/45 bg-black/90 text-yellow-400 shadow-lg backdrop-blur-md hover:border-yellow-400 hover:bg-zinc-950 transition-colors"
            onClick={() => setShowLoadingOverlay(true)}
            title={`${Math.max(visibleLoadingList.length, trackedObjectCount)} loading`}
            aria-label="Show models loading"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
              <path d="M4 7h16M4 12h10M4 17h14" strokeLinecap="round" />
            </svg>
            {(visibleLoadingList.length > 0 || trackedObjectCount > 0) && (
              <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-yellow-500 px-1 text-[9px] font-bold text-black leading-none">
                {visibleLoadingList.length || trackedObjectCount}
              </span>
            )}
          </button>
        )}

      {showLoadingOverlay &&
        createPortal(
          <DraggablePanel
            id="loading-list"
            handleLabel="Loading"
            zIndex={1350}
            className="pointer-events-auto"
            style={{ width: 'min(22rem, calc(100vw - 1rem))' }}
          >
            <div
              className="rounded-b-md border border-yellow-600/40 border-t-0 bg-zinc-950 shadow-[0_20px_50px_rgba(0,0,0,0.55)] overflow-hidden"
              role="dialog"
              aria-label="Models loading"
            >
              <div className="flex items-center justify-between gap-2 border-b border-yellow-600/25 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-[9px] uppercase tracking-[0.16em] text-yellow-500/85">Loading</p>
                  <p className="text-xs text-zinc-300 mt-0.5 truncate">
                    {visibleLoadingList.length
                      ? `${visibleLoadingList.length} model${visibleLoadingList.length !== 1 ? 's' : ''} · ${overlayProgress}%`
                      : modelFailedList.length
                        ? `${modelFailedList.length} failed`
                        : `${Math.round(progress)}%`}
                </p>
              </div>
                  <button
                  type="button"
                    onClick={() => setShowLoadingOverlay(false)}
                  className="m-0 h-7 w-7 flex items-center justify-center rounded text-zinc-500 hover:text-yellow-400"
                  aria-label="Close"
                >
                  ×
                  </button>
                </div>
              <div className="h-0.5 bg-zinc-900">
                <div
                  className="h-full bg-yellow-500/80 transition-all"
                  style={{ width: `${Math.min(100, Math.max(4, overlayProgress))}%` }}
                />
                    </div>
              <ul className="max-h-[min(16rem,50vh)] overflow-y-auto p-2 space-y-0.5">
                {visibleLoadingList.length === 0 && modelFailedList.length === 0 ? (
                  <li className="py-6 text-center text-[11px] text-zinc-500">Finalizing…</li>
                  ) : (
                  <>
                      {visibleLoadingList.map((objectName, index) => {
                        const info = loadingProgress.get(objectName);
                        const barPct =
                          info?.cached || info?.phase === 'parsing'
                            ? 100
                            : info?.indeterminate
                              ? 40
                              : Math.max(2, info?.percent || 0);
                        const label = !info
                          ? '…'
                          : info.cached
                            ? 'Cache'
                            : info.phase === 'parsing'
                              ? 'Parsing'
                              : info.indeterminate
                                ? info.loaded
                                  ? formatBytes(info.loaded)
                                  : '…'
                                : `${info.percent || 0}%`;
                        const detail =
                          info?.total > 0
                            ? `${formatBytes(info.loaded || 0)} / ${formatBytes(info.total)}`
                            : info?.loaded
                              ? formatBytes(info.loaded)
                              : undefined;
                        return (
                      <li
                        key={`load-${index}`}
                        className="rounded-sm px-2 py-1.5 text-[11px] text-zinc-300 hover:bg-yellow-500/5"
                        title={detail}
                      >
                        <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full border border-yellow-500/70 border-t-transparent animate-spin flex-shrink-0" />
                        <span className="truncate flex-1">{objectName}</span>
                          <span className="tabular-nums text-[10px] text-yellow-500/90 shrink-0">{label}</span>
                        </div>
                        <div className="mt-1 ml-5 h-0.5 overflow-hidden rounded-sm bg-zinc-800">
                          <div
                            className={`h-full bg-yellow-500/85 ${info?.indeterminate && info?.phase !== 'parsing' ? 'animate-pulse' : 'transition-all'}`}
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                        </li>
                        );
                      })}
                    {modelFailedList.map((failedObj, index) => (
                      <li
                        key={`fail-${index}`}
                        className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-[11px] border-t border-zinc-800/80"
                      >
                        <span className="h-2 w-2 rounded-full bg-red-500 flex-shrink-0" />
                          <span className="truncate flex-1 text-red-300">{failedObj.name}</span>
                          {failedObj.backgroundRetry ? (
                          <span className="text-[10px] text-yellow-500/80">Retrying…</span>
                          ) : (
                            <button
                            type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                              failedObj.retryCallback?.();
                              }}
                            className="m-0 text-[10px] font-semibold text-yellow-400 hover:text-yellow-300"
                            >
                              Retry
                            </button>
                          )}
                        </li>
                      ))}
                  </>
                  )}
              </ul>
                </div>
          </DraggablePanel>,
          document.body
        )}
        
        <div
          className="flex-grow flex items-center justify-center overflow-hidden mdv-scene-stage"
          style={{ background: 'transparent' }}
        />

        <>
            {showUnifiedPanel && isCompact && (
              <UnifiedPanel
                layoutMode="sheet"
                modelList={modelList}
                layerVisibility={layerVisibility}
                onToggleVisibility={handleToggleVisibility}
                onIsolate={handleIsolate}
                onOpacityChange={handleOpacityChange}
                isolatedKeys={isolatedKeys}
                onRefreshCatalogue={() => refreshCatalogue()}
                catalogueLoading={catalogueLoading}
                activePanel={activePanel}
                onPanelChange={setActivePanel}
                onClose={() => setShowUnifiedPanel(false)}
                panelWidth={panelWidth}
                onWidthChange={setPanelWidth}
                projectName={PROJECT_NAME}
                recentLayerKeys={recentLayerKeys}
                dockCollapsed={panelDockCollapsed}
                dockWidth={panelDockWidth}
                dockSide={panelDockSide}
                onToggleDock={handleTogglePanelDock}
                onToggleDockCollapse={handleTogglePanelDockCollapse}
                onDockWidthChange={handlePanelDockWidthChange}
                onDockToSide={handleDockToSide}
                selectedObjectName={selectedObjectName}
                onOpenStats={handleOpenStats}
              />
            )}

            {showUnifiedPanel && !panelDocked && !isCompact && (
              <UnifiedPanel
                layoutMode="float"
                modelList={modelList}
                layerVisibility={layerVisibility}
                onToggleVisibility={handleToggleVisibility}
                onIsolate={handleIsolate}
                onOpacityChange={handleOpacityChange}
                isolatedKeys={isolatedKeys}
                onRefreshCatalogue={() => refreshCatalogue()}
                catalogueLoading={catalogueLoading}
                activePanel={activePanel}
                onPanelChange={setActivePanel}
                onClose={() => setShowUnifiedPanel(false)}
                panelWidth={panelWidth}
                onWidthChange={setPanelWidth}
                projectName={PROJECT_NAME}
                recentLayerKeys={recentLayerKeys}
                dockCollapsed={panelDockCollapsed}
                dockWidth={panelDockWidth}
                dockSide={panelDockSide}
                onToggleDock={handleTogglePanelDock}
                onToggleDockCollapse={handleTogglePanelDockCollapse}
                onDockWidthChange={handlePanelDockWidthChange}
                onDockToSide={handleDockToSide}
                selectedObjectName={selectedObjectName}
                onOpenStats={handleOpenStats}
              />
            )}

            {catalogueEmpty && (
              <EmptySiteState
                projectName={PROJECT_NAME}
                catalogueError={catalogueError}
                canManageFiles={canAccessFileManagement}
                onOpenFileManagement={() => setShowFileManagement(true)}
                onOpenLayers={() => setShowUnifiedPanel(true)}
                onRefreshCatalogue={() => refreshCatalogue()}
              />
            )}

       
            <Canvas
              className="absolute inset-0"
              dpr={[1, 1.5]}
              frameloop={showFileManagement ? 'never' : 'demand'}
              events={clickOnlyEvents}
              gl={{
                antialias: true,
                alpha: true,
                preserveDrawingBuffer: false,
                powerPreference: 'high-performance',
              }}
              camera={{ position: cameraPosition, near: 1, far: 20000, fov: 35 }}
              onCreated={({ scene, gl }) => {
                scene.background = null;
                gl.setClearColor(0x000000, 0);
              }}
            >
              <SceneComponent
                modelList={modelList}
                layerVisibility={layerVisibility} 
                cameraPosition={cameraPosition}
                lookAt={lookAt}
                animateCamera={false}
                onThreeReady={setThree}
                onLayerLoadComplete={handleLayerLoadComplete}
                onLayerLoadFailed={handleLayerLoadFailed}
                showGrid={showGrid}
                sceneLookId={sceneLookId}
              />
            </Canvas>
            <ColorChange/>
            <ObjectDetails/>
            <Info/>
            {showSceneData ? (
              <SceneDataPanel
                three={three}
                layerVisibility={layerVisibility}
                onClose={handleCloseSceneData}
              />
            ) : null}
            <ScaleBar three={three} visible={sectionEnabled} />
            <ToolStatusChip
              clickType={clickType}
              canUndoHide={hideStackLen > 0}
              onUndoHide={handleUndoHide}
              onHelp={handleToggleHelp}
            />
            <HoleLegendStack modelList={modelList} />
          </>
      </div>


            </div>
      </div>
      {isCompact && (
        <>
          <MobileNav
            layersActive={showUnifiedPanel}
            onLayers={toggleLayersPanel}
            toolsActive={showMeasurements}
            onTools={toggleToolsPanel}
            drawActive={isScribbleMode}
            onDraw={handleToggleScribbleMode}
            findActive={showSearch}
            onFind={handleToggleSearch}
            viewsActive={showViews}
            onViews={toggleViewsPanel}
            moreActive={showMoreSheet || showPreferences}
            onMore={toggleMoreSheet}
            loadingCount={visibleLoadingList.length}
          />
          <MobileSheet
            open={showMoreSheet}
            title="More"
            onClose={() => setShowMoreSheet(false)}
            height="min(70dvh, 32rem)"
            zIndex={1300}
            aboveNav
          >
            <MobileMoreList
              items={[
                {
                  id: 'prefs',
                  label: 'Preferences',
                  hint: 'Background, lighting, scene data',
                  onClick: togglePreferencesPanel,
                  icon: (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 2.5v2.2M12 19.3v2.2M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.9 19.1l1.6-1.6M17.5 6.5l1.6-1.6" strokeLinecap="round" />
                    </svg>
                  ),
                },
                {
                  id: 'shot',
                  label: 'Screenshot',
                  hint: 'Save a PNG of the scene',
                  onClick: () => {
                    setShowMoreSheet(false);
                    handleScreenshot();
                  },
                  icon: (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <rect x="3" y="7" width="18" height="13" rx="1.5" />
                      <circle cx="12" cy="13.5" r="3" />
                      <path d="M9 7l1.2-2h3.6L15 7" strokeLinecap="round" />
                    </svg>
                  ),
                },
                {
                  id: 'full',
                  label: 'Fullscreen',
                  hint: document.fullscreenElement ? 'Exit fullscreen' : 'Use the whole screen',
                  onClick: async () => {
                    setShowMoreSheet(false);
                    try {
                      if (document.fullscreenElement) await document.exitFullscreen();
                      else await document.documentElement.requestFullscreen();
                    } catch (err) {
                      console.warn('[fullscreen]', err?.message || err);
                    }
                  },
                  icon: (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                },
                ...(visibleLoadingList.length > 0
                  ? [{
                      id: 'loading',
                      label: 'Loading',
                      hint: 'Models currently downloading',
                      badge: String(visibleLoadingList.length),
                      onClick: () => {
                        setShowMoreSheet(false);
                        setShowLoadingOverlay(true);
                      },
                      icon: (
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7">
                          <path d="M12 3a9 9 0 019 9" strokeLinecap="round" />
                        </svg>
                      ),
                    }]
                  : []),
                {
                  id: 'help',
                  label: 'Help',
                  hint: 'Gestures and shortcuts',
                  onClick: () => {
                    setShowMoreSheet(false);
                    setShowHelp(true);
                  },
                  icon: (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M9.5 9a2.5 2.5 0 114.2 1.8c-.8.7-1.7 1.1-1.7 2.2M12 17h.01" strokeLinecap="round" />
                    </svg>
                  ),
                },
                {
                  id: 'reload',
                  label: 'Reload',
                  hint: 'Refresh the page',
                  onClick: () => window.location.reload(),
                  icon: (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <path d="M4 12a8 8 0 0113.66-5.66M20 12a8 8 0 01-13.66 5.66" strokeLinecap="round" />
                      <path d="M17 4v4h-4M7 20v-4h4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                },
              ]}
            />
          </MobileSheet>
        </>
      )}
      <HelpOverlay open={showHelp} onClose={() => setShowHelp(false)} projectName={PROJECT_NAME} />
      <OnboardingOverlay
        open={showOnboarding}
        projectName={PROJECT_NAME}
        onClose={handleDismissOnboarding}
        onOpenHelp={() => {
          handleDismissOnboarding();
          setShowHelp(true);
        }}
      />
      {showFileManagement && canAccessFileManagement && createPortal(
        <FileManagement
          userEmail={userEmail}
          onBack={() => setShowFileManagement(false)}
        />,
        document.body
      )}
    </div>
  );
};

export default Presentation;
